import {
    AlertCircleIcon,
    CheckCircleIcon,
    DatabaseIcon,
    FileSpreadsheetIcon,
    LinkIcon,
    Loader2Icon,
    RefreshCwIcon,
    UploadIcon,
    XCircleIcon,
} from "lucide-react";
import {useRef, useState} from "react";
import {useSWRConfig} from "swr";

import {StaffingMarginDrilldownSheet} from "@components/alseasons/StaffingMarginDrilldownSheet";
import {Badge} from "@components/ui/badge";
import {Button} from "@components/ui/button";
import {Card, CardContent} from "@components/ui/card";
import {Input} from "@components/ui/input";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@components/ui/select";
import {
    Sheet,
    SheetContent,
    SheetDescription,
    SheetHeader,
    SheetTitle,
} from "@components/ui/sheet";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@components/ui/table";
import {Tabs, TabsContent, TabsList, TabsTrigger} from "@components/ui/tabs";
import {BookingsSummaryTab} from "@components/vnext/BookingsSummaryTab";
import {
    buildFyCalendar,
    type StaffingActualsGroup,
    type StaffingClientSiteCoverageData,
    type StaffingFyCalendarBuildResult,
    type StaffingUploadResult,
    uploadRateCardCsv,
    uploadClientSiteMapCsv,
    uploadGrossMarginCsv,
    useStaffingActualsMargin,
    useStaffingClientSiteCoverage,
    useStaffingMarginImportStatus,
    useStaffingRateCardSummary,
} from "@hooks/use-staffing-margin";

type GroupByKey = "customer" | "site" | "role" | "parent" | "sector" | "fy_week";

const GROUP_BY_OPTIONS: {value: GroupByKey; label: string; column: string}[] = [
    {value: "customer", label: "Customer", column: "Customer"},
    {value: "site", label: "Site", column: "Site"},
    {value: "role", label: "Role", column: "Role"},
    {value: "parent", label: "Parent", column: "Parent"},
    {value: "sector", label: "Sector", column: "Sector"},
    {value: "fy_week", label: "FY Week", column: "FY Week"},
];

// Grouping keys that depend on a promoted Client/Site mapping being active.
const MAPPING_GROUP_KEYS: GroupByKey[] = ["parent", "sector"];

function groupLabel(row: StaffingActualsGroup, key: GroupByKey): string {
    switch (key) {
        case "customer":
            return row.client || "(blank customer)";
        case "site":
            return row.service_location || "(blank site)";
        case "role":
            return row.pay_level || "(blank role)";
        case "parent":
            return row.parent || "(unmapped parent)";
        case "sector":
            return row.sector || "(unmapped sector)";
        case "fy_week":
            return row.fy_week != null
                ? `FY${row.fy_year ?? "?"} W${String(row.fy_week).padStart(2, "0")}`
                : "(unmapped week)";
    }
}

const CHECK_LABELS: Record<string, string> = {
    rows: "Rows",
    invoices: "Invoices",
    revenue: "Revenue",
    profit: "Profit",
    margin_pct: "Margin %",
};

function asNumber(value: string | number | null | undefined): number {
    const n = typeof value === "number" ? value : Number(value ?? 0);
    return Number.isFinite(n) ? n : 0;
}

function money(value: string | number | null | undefined): string {
    const n = asNumber(value);
    return n.toLocaleString("en-AU", {
        style: "currency",
        currency: "AUD",
        maximumFractionDigits: 0,
    });
}

function num(value: string | number | null | undefined): string {
    return asNumber(value).toLocaleString("en-AU", {maximumFractionDigits: 0});
}

function pct(value: string | number | null | undefined): string {
    return `${asNumber(value).toFixed(2)}%`;
}

function statusBadge(status?: string | null) {
    if (status === "promoted") {
        return <Badge className="border-green-200 bg-green-50 text-green-700">Promoted</Badge>;
    }
    if (status === "failed") {
        return <Badge variant="destructive">Failed</Badge>;
    }
    if (status === "validated") {
        return <Badge className="border-blue-200 bg-blue-50 text-blue-700">Validated</Badge>;
    }
    return <Badge variant="outline">{status || "Not loaded"}</Badge>;
}

function MetricCard({label, value, sub}: {label: string; value: string; sub?: string}) {
    return (
        <Card>
            <CardContent className="p-4">
                <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{label}</div>
                <div className="mt-1 text-2xl font-bold text-[#209DB3]">{value}</div>
                {sub && <div className="mt-1 text-xs text-muted-foreground">{sub}</div>}
            </CardContent>
        </Card>
    );
}

function LoadingState({label}: {label: string}) {
    return (
        <div className="flex items-center justify-center py-12 text-sm text-muted-foreground">
            <Loader2Icon className="mr-2 size-4 animate-spin" />
            {label}
        </div>
    );
}

function ErrorState({message}: {message: string}) {
    return (
        <div className="flex items-center justify-center py-12 text-sm text-destructive">
            <AlertCircleIcon className="mr-2 size-4" />
            {message}
        </div>
    );
}

function SummaryRows({
    rows,
    groupBy,
    onSelect,
}: {
    rows: StaffingActualsGroup[];
    groupBy: GroupByKey;
    onSelect: ((client: string) => void) | null;
}) {
    const column = GROUP_BY_OPTIONS.find((o) => o.value === groupBy)?.column ?? "Group";
    const clickable = onSelect !== null;
    return (
        <div className="rounded-md border">
            <Table>
                <TableHeader>
                    <TableRow className="bg-muted/40">
                        <TableHead>{column}</TableHead>
                        <TableHead className="text-right">Revenue</TableHead>
                        <TableHead className="text-right">Cost</TableHead>
                        <TableHead className="text-right">Profit</TableHead>
                        <TableHead className="text-right">Margin</TableHead>
                        <TableHead className="text-right">Invoices</TableHead>
                        <TableHead className="text-right">Rows</TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {rows.slice(0, 60).map((row, idx) => {
                        const label = groupLabel(row, groupBy);
                        return (
                            <TableRow
                                key={`${label}-${idx}`}
                                className={clickable ? "cursor-pointer hover:bg-muted/50" : undefined}
                                onClick={clickable ? () => onSelect!(row.client ?? "") : undefined}
                            >
                                <TableCell className={`font-medium ${clickable ? "text-[#209DB3]" : ""}`}>{label}</TableCell>
                                <TableCell className="text-right tabular-nums">{money(row.revenue)}</TableCell>
                                <TableCell className="text-right tabular-nums">{money(row.cost)}</TableCell>
                                <TableCell className="text-right tabular-nums">{money(row.profit)}</TableCell>
                                <TableCell className="text-right tabular-nums">{pct(row.margin_pct)}</TableCell>
                                <TableCell className="text-right tabular-nums">{num(row.invoice_count)}</TableCell>
                                <TableCell className="text-right tabular-nums">{num(row.row_count)}</TableCell>
                            </TableRow>
                        );
                    })}
                </TableBody>
            </Table>
        </div>
    );
}

function MarginModelTab() {
    const [groupBy, setGroupBy] = useState<GroupByKey>("customer");
    const [fyWeekInput, setFyWeekInput] = useState("");
    const fyWeek = fyWeekInput.trim();
    const filters = fyWeek ? {fy_week: fyWeek} : undefined;

    const total = useStaffingActualsMargin("total", filters);
    const grouped = useStaffingActualsMargin(groupBy, filters);
    const [selectedClient, setSelectedClient] = useState<string | null>(null);

    if (total.isLoading || grouped.isLoading) {
        return <LoadingState label="Loading Alseasons margin actuals..." />;
    }
    if (total.error || grouped.error) {
        return <ErrorState message="Could not load Alseasons margin actuals." />;
    }

    const summary = total.data?.groups?.[0];
    const activeRun = total.data?.active_run;
    if (!summary || !activeRun) {
        return <ErrorState message="No promoted Gross Margin CSV import is active yet." />;
    }

    const heading = GROUP_BY_OPTIONS.find((o) => o.value === groupBy)?.label ?? "Customer";
    const groupedRows = grouped.data?.groups ?? [];
    const needsMapping = MAPPING_GROUP_KEYS.includes(groupBy) && groupedRows.length === 0;

    return (
        <div className="space-y-5">
            <div>
                <h1 className="text-3xl font-bold">Alseasons Margin Model</h1>
                <p className="mt-1 text-sm text-muted-foreground">
                    Actual invoice-line economics from the promoted Gross Margin CSV. Forecast and rate-card logic come after mappings and cards are validated.
                </p>
            </div>
            <div className="grid gap-3 md:grid-cols-4">
                <MetricCard label="Invoice revenue" value={money(summary.revenue)} sub={fyWeek ? `FY week ${fyWeek}` : "Promoted actuals"} />
                <MetricCard label="Profit" value={money(summary.profit)} sub="Recomputed from components" />
                <MetricCard label="Weighted margin" value={pct(summary.margin_pct)} sub="Actual invoice economics" />
                <MetricCard label="Invoices / rows" value={`${num(summary.invoice_count)} / ${num(summary.row_count)}`} sub="Nonblank invoices" />
            </div>
            <Card>
                <CardContent className="p-4">
                    <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
                        <div>
                            <h2 className="text-base font-semibold">{heading} Margin Summary</h2>
                            <p className="text-xs text-muted-foreground">
                                {groupBy === "customer"
                                    ? "Click a customer to open the audit drawer. Switch the grouping to view by site, role, parent, sector, or FY week."
                                    : MAPPING_GROUP_KEYS.includes(groupBy)
                                      ? "Parent / sector come from the promoted Client/Site mapping (Data Files)."
                                      : "Grouped actuals. FY-week views require the FY calendar to be built (Data Files)."}
                            </p>
                        </div>
                        <div className="flex flex-wrap items-center gap-2">
                            <Select value={groupBy} onValueChange={(v) => setGroupBy(v as GroupByKey)}>
                                <SelectTrigger className="w-36">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {GROUP_BY_OPTIONS.map((o) => (
                                        <SelectItem key={o.value} value={o.value}>
                                            {o.label}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            <Input
                                type="number"
                                min={1}
                                max={53}
                                placeholder="FY week"
                                value={fyWeekInput}
                                onChange={(e) => setFyWeekInput(e.target.value)}
                                className="w-28"
                            />
                            {statusBadge(activeRun.status)}
                        </div>
                    </div>
                    {needsMapping ? (
                        <div className="rounded-md border border-dashed p-6 text-center text-sm text-muted-foreground">
                            No promoted Client/Site mapping is active. Upload one in Data Files to group by {groupBy}.
                        </div>
                    ) : (
                        <SummaryRows
                            rows={groupedRows}
                            groupBy={groupBy}
                            onSelect={groupBy === "customer" ? setSelectedClient : null}
                        />
                    )}
                </CardContent>
            </Card>
            <StaffingMarginDrilldownSheet
                client={selectedClient}
                onClose={() => setSelectedClient(null)}
            />
        </div>
    );
}

function CheckPills({result}: {result: StaffingUploadResult}) {
    const checks = result.payload?.checks ?? [];
    if (!checks.length) return null;
    return (
        <div className="mt-3 flex flex-wrap gap-2">
            {checks.map((c) => (
                <span
                    key={c.name}
                    className={`inline-flex items-center gap-1 rounded-md border px-2 py-1 text-xs ${
                        c.passed
                            ? "border-green-200 bg-green-50 text-green-700"
                            : "border-red-200 bg-red-50 text-red-700"
                    }`}
                >
                    {c.passed ? (
                        <CheckCircleIcon className="size-3" />
                    ) : (
                        <XCircleIcon className="size-3" />
                    )}
                    {CHECK_LABELS[c.name] ?? c.name}: {c.actual ?? "—"}
                    {!c.passed && <span className="opacity-70"> (exp {c.expected})</span>}
                </span>
            ))}
        </div>
    );
}

function GrossMarginUploadCard({onUploaded}: {onUploaded: () => void}) {
    const {mutate} = useSWRConfig();
    const inputRef = useRef<HTMLInputElement>(null);
    const [file, setFile] = useState<File | null>(null);
    const [uploading, setUploading] = useState(false);
    const [result, setResult] = useState<StaffingUploadResult | null>(null);

    const handleUpload = async () => {
        if (!file) return;
        setUploading(true);
        setResult(null);
        const res = await uploadGrossMarginCsv(file);
        setResult(res);
        setUploading(false);
        if (res.ok) {
            onUploaded();
            // Refresh the Margin Model tab's actuals (any group_by key).
            mutate(
                (key) =>
                    typeof key === "string"
                    && key.startsWith("/api/staffing-margin/actuals/margin/"),
                undefined,
                {revalidate: true},
            );
            setFile(null);
            if (inputRef.current) inputRef.current.value = "";
        }
    };

    return (
        <Card>
            <CardContent className="p-4">
                <div className="mb-3">
                    <h2 className="text-base font-semibold">Upload Gross Margin CSV</h2>
                    <p className="text-xs text-muted-foreground">
                        Validates against control totals before it replaces the active dataset. A failed upload leaves the current data live.
                    </p>
                </div>
                <div className="flex flex-wrap items-center gap-3">
                    <input
                        ref={inputRef}
                        type="file"
                        accept=".csv"
                        className="hidden"
                        onChange={(e) => {
                            setFile(e.target.files?.[0] ?? null);
                            setResult(null);
                        }}
                    />
                    <Button
                        variant="outline"
                        onClick={() => inputRef.current?.click()}
                        disabled={uploading}
                    >
                        <FileSpreadsheetIcon className="mr-2 size-4" />
                        {file ? "Change file" : "Choose CSV"}
                    </Button>
                    <span className="text-sm text-muted-foreground">
                        {file ? file.name : "No file selected"}
                    </span>
                    <Button onClick={handleUpload} disabled={!file || uploading}>
                        {uploading ? (
                            <Loader2Icon className="mr-2 size-4 animate-spin" />
                        ) : (
                            <UploadIcon className="mr-2 size-4" />
                        )}
                        {uploading ? "Validating…" : "Upload & validate"}
                    </Button>
                </div>

                {result && (
                    <div
                        className={`mt-4 rounded-md border p-3 ${
                            result.ok
                                ? "border-green-200 bg-green-50"
                                : "border-red-200 bg-red-50"
                        }`}
                    >
                        <div className="flex items-center gap-2 text-sm font-semibold">
                            {result.ok ? (
                                <>
                                    <CheckCircleIcon className="size-4 text-green-600" />
                                    <span className="text-green-800">
                                        Validation passed — new dataset promoted.
                                    </span>
                                </>
                            ) : (
                                <>
                                    <AlertCircleIcon className="size-4 text-red-600" />
                                    <span className="text-red-800">
                                        Validation failed — active dataset unchanged.
                                    </span>
                                </>
                            )}
                        </div>
                        <CheckPills result={result} />
                        {result.errors.length > 0 && (
                            <ul className="mt-2 list-disc pl-5 text-xs text-red-700">
                                {result.errors.map((e) => (
                                    <li key={e}>{e}</li>
                                ))}
                            </ul>
                        )}
                        {result.warnings.length > 0 && (
                            <p className="mt-2 text-xs text-amber-700">
                                Warnings: {result.warnings.join(", ")}
                            </p>
                        )}
                    </div>
                )}
            </CardContent>
        </Card>
    );
}

function FyCalendarBuildCard() {
    const {mutate} = useSWRConfig();
    const [building, setBuilding] = useState(false);
    const [result, setResult] = useState<StaffingFyCalendarBuildResult | null>(null);

    const handleBuild = async () => {
        setBuilding(true);
        setResult(null);
        const res = await buildFyCalendar();
        setResult(res);
        setBuilding(false);
        if (res.ok) {
            mutate(
                (key) =>
                    typeof key === "string"
                    && key.startsWith("/api/staffing-margin/actuals/margin/"),
                undefined,
                {revalidate: true},
            );
        }
    };

    return (
        <Card>
            <CardContent className="p-4">
                <div className="mb-3 flex items-start justify-between gap-3">
                    <div>
                        <h2 className="text-base font-semibold">FY Calendar</h2>
                        <p className="text-xs text-muted-foreground">
                            Builds the Alseasons FY-week spine (July-1 anchored, validated to the Grand Master) and stamps fy_year/fy_week on every promoted actual line. Required before FY-week grouping.
                        </p>
                    </div>
                    <Button onClick={handleBuild} disabled={building}>
                        {building ? (
                            <Loader2Icon className="mr-2 size-4 animate-spin" />
                        ) : (
                            <RefreshCwIcon className="mr-2 size-4" />
                        )}
                        {building ? "Building…" : "Build & backfill weeks"}
                    </Button>
                </div>
                {result && (
                    <div
                        className={`rounded-md border p-3 ${
                            result.ok ? "border-green-200 bg-green-50" : "border-red-200 bg-red-50"
                        }`}
                    >
                        {result.ok ? (
                            <div className="flex items-center gap-2 text-sm font-semibold text-green-800">
                                <CheckCircleIcon className="size-4 text-green-600" />
                                {result.fy_range
                                    ? `FY${result.fy_range[0]}–FY${result.fy_range[1]} spine built; `
                                    : ""}
                                {num(result.updated)} lines stamped
                                {result.missing_date ? `, ${num(result.missing_date)} without a shift date` : ""}.
                            </div>
                        ) : (
                            <div className="flex items-center gap-2 text-sm font-semibold text-red-800">
                                <AlertCircleIcon className="size-4 text-red-600" />
                                Build failed — actuals unchanged.
                            </div>
                        )}
                        {result.outside_spine && result.outside_spine.length > 0 && (
                            <p className="mt-2 text-xs text-amber-700">
                                Dates outside spine: {result.outside_spine.map(([fy, wk]) => `FY${fy} W${wk}`).join(", ")}
                            </p>
                        )}
                        {result.errors.length > 0 && (
                            <ul className="mt-2 list-disc pl-5 text-xs text-red-700">
                                {result.errors.map((e) => (
                                    <li key={e}>{e}</li>
                                ))}
                            </ul>
                        )}
                    </div>
                )}
            </CardContent>
        </Card>
    );
}

function SiteListSection({title, sites}: {title: string; sites: string[]}) {
    return (
        <div className="rounded-md border">
            <div className="border-b bg-muted/40 px-3 py-2 text-sm font-semibold">
                {title} ({sites.length})
            </div>
            {sites.length === 0 ? (
                <p className="px-3 py-4 text-xs text-muted-foreground">None — fully covered.</p>
            ) : (
                <ul className="max-h-72 divide-y overflow-auto text-sm">
                    {sites.map((s) => (
                        <li key={s} className="px-3 py-1.5">{s || "(blank)"}</li>
                    ))}
                </ul>
            )}
        </div>
    );
}

function ClientSiteGapsDrawer({
    coverage,
    open,
    onClose,
}: {
    coverage: StaffingClientSiteCoverageData | null;
    open: boolean;
    onClose: () => void;
}) {
    return (
        <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
            <SheetContent
                side="right"
                className="flex w-[800px] max-w-[97vw] flex-col gap-0 p-0 sm:max-w-[800px]"
            >
                <SheetHeader className="border-b px-5 py-4 text-left">
                    <SheetTitle className="text-base font-semibold">Mapping coverage gaps</SheetTitle>
                    <SheetDescription>
                        Sites seen in actuals or EOH bookings with no mapping row, plus mapping rows seen in neither.
                    </SheetDescription>
                </SheetHeader>
                <div className="min-h-0 flex-1 space-y-4 overflow-auto p-4">
                    <SiteListSection
                        title="Unmapped Gross Margin sites"
                        sites={coverage?.unmapped_gross_margin_sites ?? []}
                    />
                    <SiteListSection
                        title="Unmapped EOH booking sites"
                        sites={coverage?.unmapped_eoh_booking_sites ?? []}
                    />
                    <SiteListSection
                        title="Stale mapping rows (not in current actuals or bookings)"
                        sites={coverage?.stale_mapping_sites ?? []}
                    />
                </div>
            </SheetContent>
        </Sheet>
    );
}

function ClientSiteMappingCard() {
    const {mutate} = useSWRConfig();
    const coverage = useStaffingClientSiteCoverage(true);
    const inputRef = useRef<HTMLInputElement>(null);
    const [file, setFile] = useState<File | null>(null);
    const [uploading, setUploading] = useState(false);
    const [result, setResult] = useState<StaffingUploadResult | null>(null);
    const [gapsOpen, setGapsOpen] = useState(false);

    const data = coverage.data;
    const totals = data?.totals;
    const gaps = data?.gaps;

    const handleUpload = async () => {
        if (!file) return;
        setUploading(true);
        setResult(null);
        const res = await uploadClientSiteMapCsv(file);
        setResult(res);
        setUploading(false);
        if (res.ok) {
            coverage.mutate();
            // Parent/sector groupings depend on the mapping — revalidate actuals.
            mutate(
                (key) =>
                    typeof key === "string"
                    && key.startsWith("/api/staffing-margin/actuals/margin/"),
                undefined,
                {revalidate: true},
            );
            setFile(null);
            if (inputRef.current) inputRef.current.value = "";
        }
    };

    return (
        <Card>
            <CardContent className="p-4">
                <div className="mb-3 flex items-start justify-between gap-3">
                    <div>
                        <h2 className="text-base font-semibold">Client / Site / Xero Mapping</h2>
                        <p className="text-xs text-muted-foreground">
                            One row per site (Service Location) — parent, sector, Xero contact, anchor flag, rate-card tier. Bridges EOH bookings and invoice actuals. Blocks on overlapping windows; warns on open decisions.
                        </p>
                    </div>
                    {statusBadge(data?.has_mapping ? data?.mapping_run?.status : null)}
                </div>

                <div className="flex flex-wrap items-center gap-3">
                    <input
                        ref={inputRef}
                        type="file"
                        accept=".csv"
                        className="hidden"
                        onChange={(e) => {
                            setFile(e.target.files?.[0] ?? null);
                            setResult(null);
                        }}
                    />
                    <Button variant="outline" onClick={() => inputRef.current?.click()} disabled={uploading}>
                        <LinkIcon className="mr-2 size-4" />
                        {file ? "Change file" : "Choose CSV"}
                    </Button>
                    <span className="text-sm text-muted-foreground">{file ? file.name : "No file selected"}</span>
                    <Button onClick={handleUpload} disabled={!file || uploading}>
                        {uploading ? <Loader2Icon className="mr-2 size-4 animate-spin" /> : <UploadIcon className="mr-2 size-4" />}
                        {uploading ? "Validating…" : "Upload & validate"}
                    </Button>
                    {data?.has_mapping && (
                        <Button variant="ghost" onClick={() => setGapsOpen(true)}>
                            View gaps
                        </Button>
                    )}
                </div>

                {result && (
                    <div className={`mt-4 rounded-md border p-3 ${result.ok ? "border-green-200 bg-green-50" : "border-red-200 bg-red-50"}`}>
                        <div className="flex items-center gap-2 text-sm font-semibold">
                            {result.ok ? (
                                <>
                                    <CheckCircleIcon className="size-4 text-green-600" />
                                    <span className="text-green-800">Validation passed — mapping promoted.</span>
                                </>
                            ) : (
                                <>
                                    <AlertCircleIcon className="size-4 text-red-600" />
                                    <span className="text-red-800">Validation failed — active mapping unchanged.</span>
                                </>
                            )}
                        </div>
                        {result.errors.length > 0 && (
                            <ul className="mt-2 list-disc pl-5 text-xs text-red-700">
                                {result.errors.map((e) => <li key={e}>{e}</li>)}
                            </ul>
                        )}
                        {result.warnings.length > 0 && (
                            <p className="mt-2 text-xs text-amber-700">Warnings: {result.warnings.join(", ")}</p>
                        )}
                    </div>
                )}

                {data?.has_mapping && totals && (
                    <div className="mt-4 grid gap-3 md:grid-cols-3">
                        <div className="rounded-md border p-3">
                            <div className="flex items-center gap-2 text-sm font-semibold">
                                <CheckCircleIcon className="size-4 text-green-600" />
                                Actuals coverage
                            </div>
                            <p className="mt-1 text-xs text-muted-foreground">
                                {totals.mapped_actuals_pct == null ? "—" : pct(totals.mapped_actuals_pct)} of actuals sites mapped ({num(totals.mapped_actuals_sites)} / {num(totals.actuals_sites)}).
                            </p>
                        </div>
                        <div className="rounded-md border p-3">
                            <div className="flex items-center gap-2 text-sm font-semibold">
                                <AlertCircleIcon className="size-4 text-amber-600" />
                                Unmapped sites
                            </div>
                            <p className="mt-1 text-xs text-muted-foreground">
                                {num((data.unmapped_gross_margin_sites ?? []).length)} actuals, {num((data.unmapped_eoh_booking_sites ?? []).length)} EOH booking sites.
                            </p>
                        </div>
                        <div className="rounded-md border p-3">
                            <div className="flex items-center gap-2 text-sm font-semibold">
                                <DatabaseIcon className="size-4 text-[#209DB3]" />
                                Open decisions
                            </div>
                            <p className="mt-1 text-xs text-muted-foreground">
                                {num(gaps?.missing_xero_contact)} Xero, {num(gaps?.missing_anchor_flag)} anchor, {num(gaps?.missing_rate_card_tier)} rate-card tier missing.
                            </p>
                        </div>
                    </div>
                )}
            </CardContent>
            <ClientSiteGapsDrawer
                coverage={data ?? null}
                open={gapsOpen}
                onClose={() => setGapsOpen(false)}
            />
        </Card>
    );
}

function DraftRateCardUploadCard() {
    const {mutate} = useSWRConfig();
    const summary = useStaffingRateCardSummary(true);
    const inputRef = useRef<HTMLInputElement>(null);
    const [file, setFile] = useState<File | null>(null);
    const [uploading, setUploading] = useState(false);
    const [result, setResult] = useState<StaffingUploadResult | null>(null);

    const data = summary.data;
    const totals = data?.totals;
    const active = data?.active_run;

    const handleUpload = async () => {
        if (!file) return;
        setUploading(true);
        setResult(null);
        const res = await uploadRateCardCsv(file);
        setResult(res);
        setUploading(false);
        if (res.ok) {
            summary.mutate();
            mutate("/api/staffing-margin/import/status/?file_type=gross_margin");
            setFile(null);
            if (inputRef.current) inputRef.current.value = "";
        }
    };

    return (
        <Card>
            <CardContent className="p-4">
                <div className="mb-3 flex items-start justify-between gap-3">
                    <div>
                        <div className="flex flex-wrap items-center gap-2">
                            <h2 className="text-base font-semibold">Draft Rate Cards</h2>
                            <Badge className="border-amber-200 bg-amber-50 text-amber-700">
                                Draft / not FY27 official
                            </Badge>
                        </div>
                        <p className="mt-1 text-xs text-muted-foreground">
                            Uploads the consolidated rate-card detail file as a draft reference dataset. This validates the rate-card shape but does not drive forecast math until final FY27 cards and exceptions are approved.
                        </p>
                    </div>
                    {statusBadge(active?.status)}
                </div>

                <div className="flex flex-wrap items-center gap-3">
                    <input
                        ref={inputRef}
                        type="file"
                        accept=".csv"
                        className="hidden"
                        onChange={(e) => {
                            setFile(e.target.files?.[0] ?? null);
                            setResult(null);
                        }}
                    />
                    <Button variant="outline" onClick={() => inputRef.current?.click()} disabled={uploading}>
                        <FileSpreadsheetIcon className="mr-2 size-4" />
                        {file ? "Change file" : "Choose CSV"}
                    </Button>
                    <span className="text-sm text-muted-foreground">{file ? file.name : "No file selected"}</span>
                    <Button onClick={handleUpload} disabled={!file || uploading}>
                        {uploading ? <Loader2Icon className="mr-2 size-4 animate-spin" /> : <UploadIcon className="mr-2 size-4" />}
                        {uploading ? "Validating…" : "Upload draft cards"}
                    </Button>
                </div>

                {summary.error && (
                    <div className="mt-4 rounded-md border border-dashed p-4 text-sm text-muted-foreground">
                        Draft rate-card backend is not available yet. Once the backend slice is deployed, this card will show upload status and validation coverage.
                    </div>
                )}

                {result && (
                    <div className={`mt-4 rounded-md border p-3 ${result.ok ? "border-green-200 bg-green-50" : "border-red-200 bg-red-50"}`}>
                        <div className="flex items-center gap-2 text-sm font-semibold">
                            {result.ok ? (
                                <>
                                    <CheckCircleIcon className="size-4 text-green-600" />
                                    <span className="text-green-800">Validation passed — draft rate cards promoted.</span>
                                </>
                            ) : (
                                <>
                                    <AlertCircleIcon className="size-4 text-red-600" />
                                    <span className="text-red-800">Validation failed — active draft rate cards unchanged.</span>
                                </>
                            )}
                        </div>
                        {result.errors.length > 0 && (
                            <ul className="mt-2 list-disc pl-5 text-xs text-red-700">
                                {result.errors.map((e) => <li key={e}>{e}</li>)}
                            </ul>
                        )}
                        {result.warnings.length > 0 && (
                            <p className="mt-2 text-xs text-amber-700">Warnings: {result.warnings.join(", ")}</p>
                        )}
                    </div>
                )}

                {!summary.error && data?.has_rate_card && totals && (
                    <div className="mt-4 space-y-3">
                        <div className="grid gap-3 md:grid-cols-4">
                            <div className="rounded-md border p-3">
                                <div className="text-sm font-semibold">Source rows</div>
                                <p className="mt-1 text-xl font-bold text-[#209DB3]">{num(totals.rows)}</p>
                                <p className="text-xs text-muted-foreground">
                                    {totals.rate_line_rows ? `${num(totals.rate_line_rows)} normalized rate lines` : "Wide CSV rows"}
                                </p>
                            </div>
                            <div className="rounded-md border p-3">
                                <div className="text-sm font-semibold">Rate tables</div>
                                <p className="mt-1 text-xl font-bold text-[#209DB3]">{num(totals.distinct_rate_tables)}</p>
                                <p className="text-xs text-muted-foreground">{num(totals.distinct_source_files)} source files</p>
                            </div>
                            <div className="rounded-md border p-3">
                                <div className="text-sm font-semibold">Roles / shifts</div>
                                <p className="mt-1 text-xl font-bold text-[#209DB3]">{num(totals.distinct_pay_levels)} / {num(totals.distinct_shift_types)}</p>
                                <p className="text-xs text-muted-foreground">Pay levels / shift types</p>
                            </div>
                            <div className="rounded-md border p-3">
                                <div className="text-sm font-semibold">Rate gaps</div>
                                <p className="mt-1 text-xl font-bold text-amber-700">
                                    {num(totals.missing_or_zero_pay_rates + totals.missing_or_zero_invoice_rates)}
                                </p>
                                <p className="text-xs text-muted-foreground">Blank/zero pay or charge rates</p>
                            </div>
                        </div>

                        {(data.warnings ?? []).length > 0 && (
                            <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                                {(data.warnings ?? []).join(", ")}
                            </p>
                        )}

                        {(data.top_rate_tables ?? []).length > 0 && (
                            <div className="rounded-md border">
                                <div className="border-b bg-muted/40 px-3 py-2 text-sm font-semibold">
                                    Top rate tables by row count
                                </div>
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead>Rate table</TableHead>
                                            <TableHead className="text-right">Rows</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {(data.top_rate_tables ?? []).slice(0, 8).map((row) => (
                                            <TableRow key={row.rate_table_name}>
                                                <TableCell className="font-medium">{row.rate_table_name}</TableCell>
                                                <TableCell className="text-right tabular-nums">{num(row.row_count)}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </div>
                        )}
                    </div>
                )}

                {!summary.error && !summary.isLoading && !data?.has_rate_card && (
                    <div className="mt-4 rounded-md border border-dashed p-6 text-center text-sm text-muted-foreground">
                        <FileSpreadsheetIcon className="mx-auto mb-2 size-5" />
                        No promoted draft rate-card import yet.
                    </div>
                )}
            </CardContent>
        </Card>
    );
}

function DataFilesTab() {
    const {data, error, isLoading, mutate} = useStaffingMarginImportStatus();

    if (isLoading) return <LoadingState label="Loading Alseasons data file status..." />;
    if (error) return <ErrorState message="Could not load Alseasons data file status." />;

    const grossMargin = data?.runs?.find((run) => run.file_type === "gross_margin");
    const active = grossMargin?.active_run;

    return (
        <div className="space-y-5">
            <div>
                <h1 className="text-3xl font-bold">Data Files</h1>
                <p className="mt-1 text-sm text-muted-foreground">
                    APIs stay automated where available. Controlled uploads cover source detail, mappings, and pricing inputs the APIs do not provide in the required shape.
                </p>
            </div>
            <div className="grid gap-3 md:grid-cols-3">
                <MetricCard label="EOH / Entire OnHire" value="API" sub="Bookings, shifts, timesheets, operational detail" />
                <MetricCard label="Xero" value="API" sub="Accounting actuals and reconciliation" />
                <MetricCard label="Gross Margin CSV" value={active ? "Loaded" : "Pending"} sub="Invoice-line economics export" />
            </div>
            <GrossMarginUploadCard onUploaded={() => mutate()} />
            {active && <FyCalendarBuildCard />}
            <ClientSiteMappingCard />
            <DraftRateCardUploadCard />
            <Card>
                <CardContent className="p-4">
                    <div className="mb-4 flex items-start justify-between gap-3">
                        <div>
                            <h2 className="text-base font-semibold">Gross Margin CSV</h2>
                            <p className="text-xs text-muted-foreground">Recurring controlled file until invoice-line margin detail can be automated.</p>
                        </div>
                        {statusBadge(active?.status)}
                    </div>
                    {active ? (
                        <div className="grid gap-3 md:grid-cols-3">
                            <div className="rounded-md border p-3">
                                <div className="flex items-center gap-2 text-sm font-semibold">
                                    <CheckCircleIcon className="size-4 text-green-600" />
                                    Validation passed
                                </div>
                                <p className="mt-1 text-xs text-muted-foreground">
                                    {active.filename} promoted with {active.row_counts.valid_rows as number} rows.
                                </p>
                            </div>
                            <div className="rounded-md border p-3">
                                <div className="flex items-center gap-2 text-sm font-semibold">
                                    <DatabaseIcon className="size-4 text-[#209DB3]" />
                                    Control totals
                                </div>
                                <p className="mt-1 text-xs text-muted-foreground">
                                    Revenue {money(String(active.validation_totals.invoice_revenue ?? 0))}; profit {money(String(active.validation_totals.profit_recomputed ?? 0))}.
                                </p>
                            </div>
                            <div className="rounded-md border p-3">
                                <div className="flex items-center gap-2 text-sm font-semibold">
                                    <RefreshCwIcon className="size-4 text-amber-600" />
                                    Issues
                                </div>
                                <p className="mt-1 text-xs text-muted-foreground">
                                    {grossMargin?.issue_summary.warn ?? 0} warning, {grossMargin?.issue_summary.block ?? 0} blockers.
                                </p>
                            </div>
                        </div>
                    ) : (
                        <div className="rounded-md border border-dashed p-6 text-center text-sm text-muted-foreground">
                            <FileSpreadsheetIcon className="mx-auto mb-2 size-5" />
                            No promoted Gross Margin CSV import yet.
                        </div>
                    )}
                </CardContent>
            </Card>
        </div>
    );
}

export function AlseasonsShell() {
    return (
        <div className="flex flex-col flex-1 min-h-0 overflow-hidden">
            <div className="shrink-0 px-6 pt-6 pb-2">
                <Tabs defaultValue="margin" className="flex min-h-0 flex-1 flex-col">
                    <TabsList className="grid w-fit grid-cols-3 bg-card border p-0">
                        <TabsTrigger value="margin" className="px-4">Margin Model</TabsTrigger>
                        <TabsTrigger value="bookings" className="px-4">Bookings</TabsTrigger>
                        <TabsTrigger value="files" className="px-4">Data Files</TabsTrigger>
                    </TabsList>
                    <div className="min-h-0 flex-1 overflow-auto pt-5">
                        <TabsContent value="margin" className="m-0">
                            <MarginModelTab />
                        </TabsContent>
                        <TabsContent value="bookings" className="m-0">
                            <BookingsSummaryTab workspaceId="" scenarioId="" />
                        </TabsContent>
                        <TabsContent value="files" className="m-0">
                            <DataFilesTab />
                        </TabsContent>
                    </div>
                </Tabs>
            </div>
        </div>
    );
}
